import { View } from 'foundations-components/components/View';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import styled from 'foundations-theming/utils';
import { breakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { useScopedTranslations } from 'pricing-pages-ui-components/i18n/useScopedTranslations';
import { languageToUrl } from 'pricing-pages-ui-components/utils/languageUtils';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useContext, useRef } from 'react';
import { usePublicIntersectionObserver } from '../../hooks/usePublicIntersectionObserver';
import { PublicI18nContext } from '../../context/I18nContext';
import { PublicAeoFeatureList } from './PublicAeoFeatureList';
import { PublicAeoProductInfo } from './PublicAeoProductInfo';
import aeoTranslations from 'i2l?query=sporks!../../../lang/aeo-promo-card/en.lyaml';

/*
 * Plain divs instead of styled(Flex)/styled(View) — extending Trellis components
 * with a breakpoint override is unreliable because Trellis injects its prop-driven
 * CSS after the extension's CSS, overriding it at equal specificity.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CardLayout = styled.div.withConfig({
  displayName: "PublicAeoStandalonePromoCard__CardLayout"
})({
  display: 'flex',
  [breakpoint.tablet]: {
    flexDirection: 'column'
  }
});

/* flex 52/48 gives the left panel slightly more width on desktop.
 * In column layout (mobile) these values are harmless — auto height wins. */
const InfoPanel = styled.div.withConfig({
  displayName: "PublicAeoStandalonePromoCard__InfoPanel"
})({
  display: 'flex',
  flexDirection: 'column',
  flex: 52
});
const DividerSizer = styled.div.withConfig({
  displayName: "PublicAeoStandalonePromoCard__DividerSizer"
})({
  flexShrink: 0,
  inlineSize: '18px',
  [breakpoint.tablet]: {
    inlineSize: 'auto',
    blockSize: '18px',
    alignSelf: 'stretch'
  }
});
const FeaturePanel = styled.div.withConfig({
  displayName: "PublicAeoStandalonePromoCard__FeaturePanel"
})({
  display: 'flex',
  flex: 48
});
function getAeoProductUrl() {
  return languageToUrl({
    en: 'https://www.hubspot.com/products/aeo',
    de: 'https://www.hubspot.de/products/aeo',
    es: 'https://www.hubspot.es/products/aeo',
    pt: 'https://br.hubspot.com/products/aeo',
    fr: 'https://www.hubspot.fr/products/aeo',
    ja: 'https://www.hubspot.jp/products/aeo'
  });
}
export const PublicAeoStandalonePromoCard = ({
  monthlyPrice,
  annualPrice,
  currencyCode,
  priceLoading,
  purchaseMotions,
  onSectionViewed,
  onLearnMoreClick
}) => {
  const cardRef = useRef(null);
  const {
    i18nLoader
  } = useContext(PublicI18nContext);
  const T = useScopedTranslations(aeoTranslations, i18nLoader);
  usePublicIntersectionObserver({
    onIntersect: () => onSectionViewed === null || onSectionViewed === void 0 ? void 0 : onSectionViewed(),
    targetRef: onSectionViewed ? cardRef : undefined,
    threshold: 0.9
  });
  return /*#__PURE__*/_jsx(PageSection, {
    children: /*#__PURE__*/_jsx(T.Skeleton, {
      widths: ['100%'],
      heights: [400],
      children: /*#__PURE__*/_jsx(CardWrapper, {
        ref: cardRef,
        overflow: "hidden",
        flush: true,
        "data-test-id": "public-aeo-standalone-promo-card",
        marginBlockStart: "800",
        marginBlockEnd: "1200",
        children: /*#__PURE__*/_jsxs(CardLayout, {
          children: [/*#__PURE__*/_jsx(InfoPanel, {
            children: /*#__PURE__*/_jsx(PublicAeoProductInfo, {
              monthlyPrice: monthlyPrice,
              annualPrice: annualPrice,
              currencyCode: currencyCode,
              priceLoading: priceLoading,
              purchaseMotions: purchaseMotions,
              aeoProductUrl: getAeoProductUrl(),
              onLearnMoreClick: onLearnMoreClick
            })
          }), /*#__PURE__*/_jsx(DividerSizer, {
            children: /*#__PURE__*/_jsx(View, {
              blockSize: "100%",
              backgroundColor: "fill-accent-orange-subtle"
            })
          }), /*#__PURE__*/_jsx(FeaturePanel, {
            "data-design": "creative",
            children: /*#__PURE__*/_jsx(PublicAeoFeatureList, {})
          })]
        })
      })
    })
  });
};